import type {
  BuyflowWidget,
  DeliveryOptionCardWidget,
  GetNextBuyflowStepResponse,
  ShippingAddressForm,
  SikWidget,
} from '@/lib/TypesOpenApi';
import {proxy} from 'valtio';
import {
  type StepWidgetStore,
  StepsPath,
  stepsMap,
} from '../routing/StepsRouting';
import {useWidget} from '../routing/StepsWizardContext';

const widget = stepsMap['SELF_INSTALL_KIT']!.store;

export interface SelfInstallKitPageWidget {
  /** Base widget to relay on */
  widget: StepWidgetStore;
  content?: GetNextBuyflowStepResponse;
  /** synchronized version of widget. For internal usage within computed props */
  productWidgets: Array<SikWidget>;
  deliveryOptionWidgets: Array<DeliveryOptionCardWidget>;
  shippingWidget?: DeliveryOptionCardWidget;
  pickupWidget?: DeliveryOptionCardWidget;
  shippingAddressForm?: ShippingAddressForm;
}

export const selfInstallKitWidget = proxy<SelfInstallKitPageWidget>({
  widget,
  get content() {
    return this.widget.content;
  },

  get productWidgets() {
    return (
      this.content?.buyflowStep.buyflowWidgets
        ?.map(({sikWidget}: BuyflowWidget) => {
          return sikWidget!;
        })
        .filter((w: BuyflowWidget) => !!w) || []
    );
  },

  get shippingAddressForm() {
    return this.productWidgets?.[0]?.shippingAddressForm;
  },

  get deliveryOptionWidgets() {
    if (!this.productWidgets.length) {
      return [];
    }

    return this.productWidgets.reduce(
      (allOptions: Array<DeliveryOptionCardWidget>, widget: SikWidget) => {
        return [...allOptions, ...(widget.deliveryOptionWidgets || [])];
      },
      [],
    );
  },

  get shippingWidget() {
    return this.deliveryOptionWidgets.find(
      (widget: DeliveryOptionCardWidget) => widget.deliveryType === 'SHIPPING',
    );
  },

  get pickupWidget() {
    return this.deliveryOptionWidgets.find(
      (widget: DeliveryOptionCardWidget) => widget.deliveryType === 'PICKUP',
    );
  },
});

export const useSelfInstallKitWidget = () =>
  useWidget(StepsPath.SelfInstallKit, selfInstallKitWidget);
